import {
  Badge,
  Button,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
} from "reactstrap";
import Checkbox from "utils/Checkbox";

import React, { useState, useEffect } from "react";
import { getApiDataService, postApiDataService } from "services/apiServices";
import ButtonLoading from "./../ButtonLoading";
import { UNIT_PRICE_API, UNIT_UPDATE_API } from "services/CONSTANTS";
import { ConfirmDialog } from "components/shared";

const UnitPriceEdit = ({ game, region }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gameUnits, setGameUnits] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const gameId = game?.id || null;
  const regionId = region?.id || null;

  let api = UNIT_PRICE_API;
  if (gameId) api += `?game_id=${gameId}`;
  if (regionId) api += `&region_id=${regionId}`;

  useEffect(() => {
    if (gameId && regionId) {
      fetchData();
    }
  }, [api, gameId, regionId]);

  const fetchData = () => {
    setIsLoading(true);
    getApiDataService(api)
      .then((response) => {
        setGameUnits(response.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("error", error);
        setIsLoading(false);
      });
  };

  const addUnitHandler = () => {
    setGameUnits([...gameUnits, { types: [] }]);
  };

  const confirmDeleteUnit = () => {
    if (deleteIndex === null) return;
    setGameUnits(gameUnits.filter((_, index) => index !== deleteIndex));
    setDeleteIndex(null);
  };

  const saveGameUnitPrice = (e) => {
    if (e) e.preventDefault();
    const data = [];
    setIsSaving(true);
    gameUnits.forEach((item) => {
      data.push({
        item_name: item.item_name,
        price_amount: item.price_amount,
        types: item.types,
      });
    });
    postApiDataService(UNIT_UPDATE_API(gameId, regionId), {
      item_prices: data,
    })
      .then(() => {
        setIsSaving(false);
      })
      .catch((error) => {
        setIsSaving(false);
        console.log("error", error);
      });
  };

  const handleItemNameChange = (e, index) => {
    const temp = [...gameUnits];
    temp[index].item_name = e.target.value;
    setGameUnits(temp);
  };

  const handleItemPriceChange = (e, index) => {
    const temp = [...gameUnits];
    const raw = e.target.value;
    temp[index].price_amount = raw === "" ? "" : parseInt(raw, 10);
    setGameUnits(temp);
  };

  const handleCheckboxChange = (value, index) => {
    const temp = [...gameUnits];
    if (temp[index]?.types != undefined && temp[index]?.types?.length > 0) {
      if (temp[index]?.types?.includes(value)) {
        temp[index].types = temp[index].types.filter((item) => item !== value);
      } else {
        temp[index].types = [...temp[index].types, value];
      }
    } else {
      temp[index].types = [value];
    }
    setGameUnits(temp);
  };

  if (isLoading) {
    return (
      <div className="akh-unit-price-skeleton">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="akh-skeleton" style={{ width: "8rem", height: "1rem" }} />
          <div className="akh-skeleton" style={{ width: "7rem", height: "2rem" }} />
        </div>
        <div className="akh-skeleton-card mb-3" />
        <div className="akh-skeleton-card mb-3" />
        <div className="akh-skeleton-card" />
      </div>
    );
  }

  return (
    <>
      <Form onSubmit={saveGameUnitPrice} className="akh-unit-price-edit">
        <hr className="my-3" />
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <h6 className="mb-2 mb-md-4 heading-small text-muted">
            Game Unit
            {region?.country_name ? ` — ${region.country_name}` : ""}
          </h6>
          <Button
            type="button"
            size="sm"
            color="primary"
            className="mb-2 mb-md-4"
            onClick={addUnitHandler}
          >
            Add Game Unit
          </Button>
        </div>

        {gameUnits.length === 0 ? (
          <div className="akh-empty-state">
            <i className="fas fa-box-open" aria-hidden="true" />
            <p className="mb-3">No game units for this region yet.</p>
            <Button type="button" color="primary" size="sm" onClick={addUnitHandler}>
              Add Game Unit
            </Button>
          </div>
        ) : (
          <div className="akh-unit-price-list pb-5">
            {gameUnits.map((item, index) => (
              <div key={index} className="akh-unit-price-row mb-3">
                <Row className="align-items-end">
                  <Col xs="auto" className="mb-2">
                    <Badge color="primary" pill className="font-mono">
                      {index + 1}
                    </Badge>
                  </Col>
                  <Col xs="12" md="4" className="mb-2">
                    <FormGroup className="mb-0">
                      <label
                        className="form-control-label"
                        htmlFor={`input-item-name-${index}`}
                      >
                        Unit/Item
                      </label>
                      <Input
                        className="form-control-alternative"
                        id={`input-item-name-${index}`}
                        placeholder="Unit"
                        type="text"
                        value={item?.item_name || ""}
                        onChange={(e) => handleItemNameChange(e, index)}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="12" md="3" className="mb-2">
                    <FormGroup className="mb-0">
                      <label
                        className="form-control-label"
                        htmlFor={`input-item-price-${index}`}
                      >
                        Price ({region?.currency_code || item?.region?.currency_code || ""})
                      </label>
                      <Input
                        value={item?.price_amount ?? ""}
                        onChange={(e) => handleItemPriceChange(e, index)}
                        className="form-control-alternative font-mono"
                        id={`input-item-price-${index}`}
                        placeholder={`price(${region?.currency_code || ""})`}
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="12" md="3" className="mb-2">
                    <label className="form-control-label d-block">Type</label>
                    <div className="d-flex align-items-center flex-wrap">
                      <Checkbox
                        status={item?.types?.includes("app")}
                        label="app"
                        forType={"app" + index + (region?.country_code || "")}
                        onClick={() => handleCheckboxChange("app", index)}
                      />
                      <Checkbox
                        status={item?.types?.includes("pos")}
                        label="pos"
                        forType={"pos" + index + (region?.country_code || "")}
                        onClick={() => handleCheckboxChange("pos", index)}
                      />
                    </div>
                  </Col>
                  <Col xs="12" md="auto" className="mb-2">
                    <Button
                      type="button"
                      size="sm"
                      color="danger"
                      onClick={() => setDeleteIndex(index)}
                    >
                      Delete
                    </Button>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}

        <div className="akh-sticky-save-bar">
          <div className="akh-sticky-save-bar__inner">
            <span className="akh-sticky-save-bar__meta text-muted d-none d-md-inline">
              {gameUnits.length} unit{gameUnits.length === 1 ? "" : "s"}
              {region?.currency_code ? ` · ${region.currency_code}` : ""}
            </span>
            <div className="akh-sticky-save-bar__action">
              <ButtonLoading
                color="success"
                type="submit"
                loading={isSaving}
                disabled={isSaving}
              >
                Save
              </ButtonLoading>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmDialog
        isOpen={deleteIndex !== null}
        toggle={() => setDeleteIndex(null)}
        title="Delete unit"
        message="Remove this game unit from the list? Changes are saved when you click Save."
        confirmText="Delete"
        confirmColor="danger"
        onConfirm={confirmDeleteUnit}
      />
    </>
  );
};

export default React.memo(UnitPriceEdit);
